// src/redux/apis/auth.ts

import api, { apiPost } from '../../utils/axios';
import { API_ENDPOINTS } from '../../config/environment';

interface LoginCredentials {
  email: string;
  password: string;
}

interface ForgotPasswordData {
  email: string;
}

interface LoginResponse {
  data: any;
  success: boolean;
  token: string;
  user: {
    id: string;
    email: string;
    name: string;
    role: string;
    avatar?: string;
  };
  message?: string;
}

interface ForgotPasswordResponse {
  success: boolean;
  message: string;
}

interface ResetPasswordData {
  token: string;
  newPassword: string;
}

interface OTPVerificationData {
  email: string;
  otp: string;
}

// Real API functions using axios
export const loginAPI = async (credentials: LoginCredentials): Promise<LoginResponse> => {
  try {
    const response = await apiPost<LoginResponse>(API_ENDPOINTS.AUTH.LOGIN, credentials);
    return response;
  } catch (error: any) {
    // Handle specific error cases
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    } else if (error.message) {
      throw new Error(error.message);
    } else {
      throw new Error('Login failed. Please try again.');
    }
  }
};

export const forgotPasswordAPI = async (data: ForgotPasswordData): Promise<ForgotPasswordResponse> => {
  try {
    const response = await apiPost<ForgotPasswordResponse>(API_ENDPOINTS.AUTH.FORGOT_PASSWORD, data);
    return response;
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    } else if (error.message) {
      throw new Error(error.message);
    } else {
      throw new Error('Failed to send password reset email. Please try again.');
    }
  }
};

export const resetPasswordAPI = async (data: ResetPasswordData): Promise<ForgotPasswordResponse> => {
  try {
    const response = await apiPost<ForgotPasswordResponse>(API_ENDPOINTS.AUTH.RESET_PASSWORD, data);
    return response;
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    } else if (error.message) {
      throw new Error(error.message);
    } else {
      throw new Error('Failed to reset password. Please try again.');
    }
  }
};

export const verifyOTPAPI = async (data: OTPVerificationData): Promise<ForgotPasswordResponse> => {
  try {
    const response = await apiPost<ForgotPasswordResponse>(API_ENDPOINTS.AUTH.VERIFY_OTP, data);
    return response;
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    } else if (error.message) {
      throw new Error(error.message);
    } else {
      throw new Error('Failed to verify OTP. Please try again.');
    }
  }
};

// Additional auth APIs
export const refreshTokenAPI = async (): Promise<{ token: string }> => {
  try {
    const response = await apiPost<{ token: string }>(API_ENDPOINTS.AUTH.REFRESH_TOKEN);
    return response;
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    } else if (error.message) {
      throw new Error(error.message);
    } else {
      throw new Error('Failed to refresh token.');
    }
  }
};

export const logoutAPI = async (): Promise<{ message: string }> => {
  try {
    const response = await apiPost<{ message: string }>(API_ENDPOINTS.AUTH.LOGOUT);
    return response;
  } catch (error: any) {
    console.log(error);
    // Don't throw error for logout, just return success
    return { message: 'Logged out successfully' };
  }
};

export const getProfileAPI = async (): Promise<LoginResponse['user']> => {
  try {
    const response = await api.get(API_ENDPOINTS.AUTH.PROFILE).then(res => res.data);
    return response.user;
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    } else if (error.message) {
      throw new Error(error.message);
    } else {
      throw new Error('Failed to fetch profile.');
    }
  }
};
